import { useEffect, useState } from 'react';
import { selectTenantPrompt } from '../../copy';
import { Create } from '@refinedev/antd';
import { useCreate } from '@refinedev/core';
import { Empty, message } from 'antd';
import { useNavigate } from 'react-router';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { useUnsavedEditGuard } from '../../scope/unsavedChanges';
import { QuestionForm } from './QuestionForm';
import type { QuestionPayload } from './types';

/**
 * Create an inventory question for the currently-scoped tenant. The company is
 * NOT a form field — it comes from the global "Operate as tenant" scope and is
 * threaded to the backend as `?companyId=` via `meta.companyId` (the
 * dataProvider's create honors an explicit meta.companyId). If no tenant is
 * scoped, we prompt instead of firing a 422.
 */
export function QuestionCreate() {
  const navigate = useNavigate();
  const { companyId } = useCompanyScope();
  const { mutate } = useCreate();
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Announce an in-flight draft so a tenant switch confirms-before-discard (see
  // scope/unsavedChanges). Without it, `companyId` — read live above and closed
  // over in onSubmit — would silently retarget the draft to the newly-scoped
  // tenant on Save.
  useUnsavedEditGuard(dirty);

  // A committed scope switch remounts the form (key={companyId} below) to clear
  // the stale draft; reset our own dirty flag alongside it so nothing lingers.
  useEffect(() => {
    setDirty(false);
  }, [companyId]);

  function onSubmit(payload: QuestionPayload) {
    if (!companyId) {
      message.error('Select a tenant first');
      return;
    }
    setSubmitting(true);
    mutate(
      { resource: 'questions', values: payload, meta: { companyId } },
      {
        onSuccess: () => {
          message.success('Question created');
          navigate('/questions');
        },
        onError: (err) =>
          message.error((err as { message?: string }).message ?? 'Create failed'),
        onSettled: () => setSubmitting(false),
      },
    );
  }

  return (
    <Create title="New question" goBack={false} footerButtons={() => null}>
      {companyId ? (
        <QuestionForm
          // Remount on a tenant switch so a draft composed for one tenant can't
          // survive (and be silently saved) under another.
          key={companyId}
          showDefault
          submitting={submitting}
          onSubmit={onSubmit}
          onDirtyChange={setDirty}
          onCancel={() => navigate('/questions')}
        />
      ) : (
        <Empty
          description={selectTenantPrompt('add a question to its inventory')}
          style={{ padding: '48px 0' }}
        />
      )}
    </Create>
  );
}
